/** Section — PROMOTIONS. One organization, several contests.
 *
 *  Each promotion carries its own league, game choice, prizes, odds, wording
 *  and branding. The bar keeps ONE card and ONE iPad link: switching only
 *  changes which promotion is live, so nothing is re-entered and nothing is
 *  reset. The promotion that was live is stored server side, so a restart or a
 *  republish cannot flip it.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Loader2, Plus, Radio, Smartphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { btn } from './buttons';

/** The star-player add-on, for the promotion being edited. Default OFF; it
 *  needs the score rounds on and tonight's game chosen, because the player
 *  list comes from that game. */
const StarPanel = ({ cfg, orgId, promotionId, onSave, onReload }) => {
    const { t } = useTranslation();
    const star = cfg.star || {};
    const [busy, setBusy] = useState('');
    const [pick, setPick] = useState('');
    // The tick must stay where it was put while the save is in flight, or it
    // snaps back for a second and reads as "it did not work".
    const [ticking, setTicking] = useState(null);

    useEffect(() => { setTicking(null); }, [star.enabled]);

    const save = async (patch) => {
        setBusy('save');
        try { await onSave(patch); } finally { setBusy(''); }
    };

    const toggleStar = async (on) => {
        setTicking(on);
        try { await save({ star_pick_enabled: on }); }
        catch (_e) { setTicking(null); }
    };

    const markScored = async () => {
        if (!pick) return;
        setBusy('override');
        try {
            const res = await api.post('/goal-game/admin/star/override', {
                organization_id: orgId, promotion_id: promotionId || null, player_id: pick,
            });
            const n = res.data.already_scratched;
            toast.success(`Marked: ${res.data.player.name}`
                + (n ? ` — ${n} table(s) had already scratched and keep their prize` : ''));
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not mark that');
        } finally { setBusy(''); }
    };

    const field = 'w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm';

    // Pick your winner and the fight-card settings stand on their own: a league
    // with no player list still has a winner, and a UFC card has fourteen.
    const winnerPanel = (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2 space-y-2"
             data-testid="gg-winner-panel">
            <label className="flex items-start gap-2">
                <input type="checkbox" checked={!!cfg.winner_pick_enabled}
                       disabled={busy === 'save'}
                       onChange={(e) => save({ winner_pick_enabled: e.target.checked })}
                       data-testid="gg-winner-enabled" className="mt-1" />
                <span className="text-sm">
                    <span className="font-medium">Pick your winner</span>
                    <span className="block text-[11px] text-slate-500">
                        The fan taps one of tonight&apos;s two teams, or a winner in
                        every fight on a UFC card. Shown above Pick your star on the
                        fan screen, off unless you switch it on.
                    </span>
                </span>
            </label>
            <label className="block max-w-[190px]">
                <span className="block text-[11px] text-slate-500 mb-1">Points if they win</span>
                <input type="number" min={0} max={1000000}
                       defaultValue={cfg.winner_points ?? 5000}
                       onBlur={(e) => save({ winner_points: Number(e.target.value) })}
                       data-testid="gg-winner-points"
                       className={`${field} tabular-nums`} />
            </label>
            {/* A FIGHT CARD night: a bonus on the main event, and whether the
                prelims count at all. */}
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-2 space-y-2"
                 data-testid="gg-card-panel">
                <p className="text-[11px] font-medium">On a UFC card</p>
                <p className="text-[11px] text-slate-500">
                    The player picks a winner in every fight that counts, and the
                    points above are paid per fight called right. A card has more
                    fights than a game has goals, so set Max rounds in Live-game
                    automation to at least the number of fights.
                </p>
                <label className="block max-w-[190px]">
                    <span className="block text-[11px] text-slate-500 mb-1">Main-event bonus</span>
                    <input type="number" min={0} max={1000000}
                           defaultValue={cfg.card_main_event_bonus ?? 0}
                           onBlur={(e) => save({ card_main_event_bonus: Number(e.target.value) })}
                           data-testid="gg-card-bonus"
                           className={`${field} tabular-nums`} />
                </label>
                <label className="block max-w-[230px]">
                    <span className="block text-[11px] text-slate-500 mb-1">Fights that count</span>
                    <select value={cfg.card_counts || 'all'}
                            onChange={(e) => save({ card_counts: e.target.value })}
                            data-testid="gg-card-counts" className={field}>
                        <option value="all">The whole card, prelims included</option>
                        <option value="main_card">Main card only</option>
                    </select>
                </label>
                {(cfg.card_counts || 'all') === 'main_card' && (
                    <label className="block max-w-[190px]">
                        <span className="block text-[11px] text-slate-500 mb-1">Fights on the main card</span>
                        <input type="number" min={1} max={20}
                               defaultValue={cfg.card_main_card_size ?? 5}
                               onBlur={(e) => save({ card_main_card_size: Number(e.target.value) })}
                               data-testid="gg-card-main-size"
                               className={`${field} tabular-nums`} />
                    </label>
                )}
            </div>
        </div>
    );

    // THE SAFETY VALVE — a cap on how many players may be on a screen at once.
    // Off unless a number is set. Players already in keep playing; only a NEW
    // player sees the message, and the limit comes off in one tap.
    const limit = Number(cfg.max_online || 0);
    const valvePanel = (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2 space-y-2"
             data-testid="gg-valve-panel">
            <label className="flex items-start gap-2">
                <input type="checkbox" checked={cfg.poll_pacing !== false} disabled={busy === 'save'}
                       onChange={(e) => save({ poll_pacing: e.target.checked })}
                       data-testid="gg-pacing-enabled" className="mt-1" />
                <span className="text-sm">
                    <span className="font-medium">Pace the player screens</span>
                    <span className="block text-[11px] text-slate-500">
                        The server tells each phone how often to check in: every 2 seconds
                        in the bar, every 5 at home, and every 20 when no game is on or
                        due in the next 15 minutes. A goal still reaches a phone in the bar
                        in about 3 seconds and at home in about 6. Switch it off and every
                        phone checks in every 2 seconds, as it always did.
                    </span>
                </span>
            </label>
            <label className="flex items-start gap-2">
                <input type="checkbox" checked={limit > 0} disabled={busy === 'save'}
                       onChange={(e) => save({ max_online: e.target.checked ? (limit || 500) : 0 })}
                       data-testid="gg-valve-enabled" className="mt-1" />
                <span className="text-sm">
                    <span className="font-medium">Maximum players online at once</span>
                    <span className="block text-[11px] text-slate-500">
                        Off by default. Players already in keep playing with no change —
                        only a new player sees the message below.
                    </span>
                </span>
            </label>
            {limit > 0 && (
                <>
                    <label className="block max-w-[190px]">
                        <span className="block text-[11px] text-slate-500 mb-1">Players at once</span>
                        <input type="number" min={1} max={1000000} defaultValue={limit}
                               onBlur={(e) => save({ max_online: Number(e.target.value) })}
                               data-testid="gg-valve-limit"
                               className={`${field} tabular-nums`} />
                    </label>
                    <div className="rounded-lg bg-slate-900 text-white p-3 text-center"
                         data-testid="gg-valve-preview">
                        <p className="text-[11px] uppercase tracking-wide opacity-50 mb-1">
                            What a new player sees
                        </p>
                        <p className="text-base font-bold">{t('goalGame.fullTitle')}</p>
                        <p className="text-sm opacity-80">{t('goalGame.fullBody')}</p>
                    </div>
                    <button type="button" onClick={() => save({ max_online: 0 })}
                            disabled={busy === 'save'} data-testid="gg-valve-off"
                            className="rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-xs font-semibold">
                        Turn the limit off
                    </button>
                </>
            )}
        </div>
    );

    if (!star.supported) {
        return (
            <div className="space-y-3" data-testid="gg-star-panel">
                {winnerPanel}
                {valvePanel}
                <p className="text-[11px] text-slate-500" data-testid="gg-star-unsupported">
                    Star player pick is not available for this league — its feed carries no
                    player list and no scorer.
                </p>
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3"
             data-testid="gg-star-panel">
            {winnerPanel}
            {valvePanel}
            <label className="flex items-start gap-2">
                <input type="checkbox" checked={ticking === null ? !!star.enabled : ticking}
                       disabled={(!star.rounds_on && !star.enabled) || busy === 'save'}
                       onChange={(e) => toggleStar(e.target.checked)}
                       data-testid="gg-star-enabled" className="mt-1" />
                <span className="text-sm">
                    <span className="font-medium">Star player pick</span>
                    <span className="block text-[11px] text-slate-500">
                        Double the prize when my table&apos;s player scores. Optional add-on,
                        off unless you switch it on.
                    </span>
                </span>
            </label>
            {!star.rounds_on && !star.enabled && (
                <p className="text-[11px] text-slate-500" data-testid="gg-star-needs-rounds">
                    Switch the score rounds on and choose tonight&apos;s game in Live-game
                    automation first — the player list comes from that game.
                </p>
            )}
            {star.enabled && (
                <>
                    <label className="block">
                        <span className="block text-xs font-medium text-slate-500 mb-1">
                            What counts as a score for a player
                        </span>
                        <select value={star.score_rule} className={field}
                                disabled={busy === 'save'}
                                onChange={(e) => save({ star_score_rule: e.target.value })}
                                data-testid="gg-star-rule">
                            <option value="score_only">A score only (assists and kicks never count)</option>
                            <option value="any_score">Any point credited to a player</option>
                        </select>
                    </label>
                    <p className="text-[11px] text-slate-500" data-testid="gg-star-source">
                        {star.auto_scorer
                            ? 'Scorer read automatically from the feed, which names the player on every score.'
                            : 'This league\u2019s feed names no player on a score, so doubling happens only when you mark it below.'}
                        {star.players?.length
                            ? ` ${star.players.length} players loaded${star.roster_source === 'lineup' ? " from tonight's lineup" : ' from both teams'}.`
                            : star.rounds_on
                                ? ` Lineup not posted yet for ${star.game_label || "tonight's game"} — the list appears here once the teams are announced, and a fan picking before then sees the same message. You can leave this switched on.`
                                : ' No player list yet — choose tonight\u2019s game in Live-game automation.'}
                    </p>
                    <div className="space-y-1">
                        <span className="block text-xs font-medium text-slate-500">Picks tonight</span>
                        {(star.counts || []).length === 0 ? (
                            <p className="text-[11px] text-slate-500" data-testid="gg-star-counts-empty">
                                No table has picked yet tonight.
                            </p>
                        ) : (
                            <ul className="text-[11px] text-slate-600 dark:text-slate-300 space-y-0.5"
                                data-testid="gg-star-counts">
                                {star.counts.map((row) => (
                                    <li key={row.player_id} data-testid={`gg-star-count-${row.player_id}`}>
                                        {row.name}{row.number ? ` #${row.number}` : ''} — {row.tables} table(s)
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 items-end">
                        <label className="block flex-1 min-w-[180px]">
                            <span className="block text-xs font-medium text-slate-500 mb-1">
                                Mark the last round as scored by
                            </span>
                            <select value={pick} onChange={(e) => setPick(e.target.value)}
                                    className={field} data-testid="gg-star-override-select">
                                <option value="">Choose a player…</option>
                                {(star.players || []).map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}{p.number ? ` #${p.number}` : ''}{p.position ? ` · ${p.position}` : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button type="button" onClick={markScored}
                                disabled={!pick || busy === 'override'}
                                data-testid="gg-star-override" className={btn.smallSecondary}>
                            {busy === 'override' ? <Loader2 className="animate-spin" size={14} /> : 'Mark as scored'}
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-500">
                        Use this when the feed is wrong or late. It names the scorer on the
                        round that is already open; a table that has already scratched keeps
                        the prize it drew. Every use is logged with your email.
                    </p>
                </>
            )}
        </div>
    );
};

export const GoalPromotionBlock = ({ cfg, orgId, promotionId, onPick, onReload, onSave }) => {
    const promotions = cfg.promotions || [];
    const leagues = cfg.leagues || [];
    const [busy, setBusy] = useState('');
    const [name, setName] = useState('');
    const [league, setLeague] = useState('nhl');

    const call = async (key, fn) => {
        setBusy(key);
        try {
            await fn();
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save');
        } finally {
            setBusy('');
        }
    };

    const create = () => call('create', async () => {
        const res = await api.post('/goal-game/admin/promotions', {
            organization_id: orgId, name: name.trim() || 'New promotion', league,
        });
        const fresh = (res.data.promotions || []).find(
            (p) => p.promotion_id && !promotions.some((o) => o.promotion_id === p.promotion_id));
        setName('');
        if (fresh) onPick(fresh.promotion_id);
        toast.success('Promotion added');
    });

    const setLeagueFor = (pid, value) => call(`league-${pid || 'main'}`, async () => {
        await api.patch('/goal-game/admin/promotions', {
            organization_id: orgId, promotion_id: pid, league: value,
        });
        toast.success('League saved — pick tonight\u2019s game in Live-game automation');
    });

    const rename = (pid, value) => call(`name-${pid || 'main'}`, async () => {
        await api.patch('/goal-game/admin/promotions', {
            organization_id: orgId, promotion_id: pid, name: value,
        });
    });

    const makeLive = (pid) => call(`live-${pid || 'main'}`, async () => {
        await api.post('/goal-game/admin/promotions/activate', {
            organization_id: orgId, promotion_id: pid,
        });
        toast.success('That promotion is live now');
    });

    const setTarget = (pid) => call(`target-${pid || 'main'}`, async () => {
        await api.post('/goal-game/admin/promotions/switch-target', {
            organization_id: orgId, promotion_id: pid,
        });
        toast.success('The iPad button will switch to this one');
    });

    // A copy starts as the same look, wording, prizes and odds. It is standby
    // until the operator switches to it, and it carries no card, no links and
    // no game choice.
    const duplicate = (pid, sourceName) => call(`copy-${pid || 'main'}`, async () => {
        const res = await api.post('/goal-game/admin/promotions/duplicate', {
            organization_id: orgId, promotion_id: pid,
            name: `${sourceName || 'Promotion'} copy`.slice(0, 60),
        });
        const fresh = res.data.created?.promotion_id;
        if (fresh) onPick(fresh);
        toast.success('Copied — rename it, pick its league, then edit what you like');
    });

    const remove = (pid, pName) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete "${pName}"? Its prizes, wording and game choice go with it. This cannot be undone.`)) return;
        call(`del-${pid}`, async () => {
            await api.post('/goal-game/admin/promotions/delete', {
                organization_id: orgId, promotion_id: pid,
            });
            if ((promotionId || null) === pid) onPick(null);
            toast.success('Promotion deleted');
        });
    };

    const field = 'w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm';

    return (
        <div className="space-y-4" data-testid="gg-promotions">
            <p className="text-xs text-slate-500">
                Each promotion is its own contest in this same venue — its own league,
                game, prizes, odds, wording and branding. The table card and the links
                never change. <strong>Duplicate</strong> starts a new one as an exact copy
                of this one&apos;s look, wording, prizes and odds, so nothing is rebuilt
                from scratch: rename it, pick its league, change what you like.
            </p>

            {promotions.map((p) => {
                const pid = p.promotion_id;
                const key = pid || 'main';
                const selected = (promotionId || null) === (pid || null);
                const isTarget = (cfg.switch_promotion_id || null) === (pid || null);
                return (
                    <div key={key}
                         data-testid={`gg-promo-row-${key}`}
                         className={`rounded-2xl border p-3 space-y-3 ${selected
                             ? 'border-amber-400 bg-amber-50/40 dark:bg-amber-500/5'
                             : 'border-slate-200 dark:border-slate-700'}`}>
                        <div className="flex flex-wrap items-center gap-2">
                            <button type="button" onClick={() => onPick(pid)}
                                    data-testid={`gg-promo-pick-${key}`}
                                    className={selected ? btn.smallPrimary : btn.smallSecondary}>
                                {selected ? 'Editing' : 'Edit this one'}
                            </button>
                            {p.is_live && (
                                <span data-testid={`gg-promo-live-${key}`}
                                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                                    <Radio size={12} /> Live now
                                </span>
                            )}
                            {isTarget && (
                                <span data-testid={`gg-promo-target-${key}`}
                                      className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 dark:bg-sky-900/40 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-300">
                                    <Smartphone size={12} /> iPad button
                                </span>
                            )}
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">Name</span>
                                <input
                                    defaultValue={p.name}
                                    onBlur={(e) => {
                                        const value = e.target.value.trim();
                                        if (value && value !== p.name) rename(pid, value);
                                    }}
                                    data-testid={`gg-promo-name-${key}`}
                                    className={field}
                                />
                            </label>
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">League</span>
                                <select
                                    value={p.league}
                                    onChange={(e) => setLeagueFor(pid, e.target.value)}
                                    disabled={busy === `league-${key}`}
                                    data-testid={`gg-promo-league-${key}`}
                                    className={field}
                                >
                                    {leagues.map((l) => (
                                        <option key={l.id} value={l.id}>{l.label}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => makeLive(pid)}
                                    disabled={p.is_live || busy === `live-${key}`}
                                    data-testid={`gg-promo-make-live-${key}`}
                                    className={btn.smallSecondary}>
                                {busy === `live-${key}` ? <Loader2 className="animate-spin" size={14} /> : 'Make this live'}
                            </button>
                            <button type="button" onClick={() => setTarget(pid)}
                                    disabled={isTarget || busy === `target-${key}`}
                                    data-testid={`gg-promo-set-target-${key}`}
                                    className={btn.smallSecondary}>
                                Use for the iPad button
                            </button>
                            <button type="button" onClick={() => duplicate(pid, p.name)}
                                    disabled={busy === `copy-${key}`}
                                    data-testid={`gg-promo-duplicate-${key}`}
                                    className={btn.smallSecondary}>
                                {busy === `copy-${key}`
                                    ? <Loader2 className="animate-spin" size={14} />
                                    : <span className="inline-flex items-center gap-1.5"><Copy size={13} /> Duplicate</span>}
                            </button>
                            {!p.is_primary && (
                                <button type="button" onClick={() => remove(pid, p.name)}
                                        disabled={p.is_live || busy === `del-${key}`}
                                        title={p.is_live ? 'The live promotion cannot be deleted' : 'Delete this promotion'}
                                        data-testid={`gg-promo-delete-${key}`}
                                        className={btn.smallSecondary}>
                                    {busy === `del-${key}`
                                        ? <Loader2 className="animate-spin" size={14} />
                                        : <span className="inline-flex items-center gap-1.5 text-rose-600 dark:text-rose-400"><Trash2 size={13} /> Delete</span>}
                                </button>
                            )}
                        </div>
                        <p className="text-[11px] text-slate-500">
                            Window {p.window_seconds}s · 1 in {p.odds_one_in}
                        </p>
                        {p.league === 'cfl' && (
                            <p className="text-[11px] rounded-xl border border-amber-400/60 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-amber-800 dark:text-amber-300"
                               data-testid={`gg-promo-cfl-warning-${key}`}>
                                No games are coming back from this feed. The public CFL
                                source we read returns an empty schedule for every date
                                (it reports itself as season 2023), so tonight&apos;s game
                                cannot be chosen and nothing can fire automatically. The
                                manual button still works.
                            </p>
                        )}
                        {selected && <StarPanel cfg={cfg} orgId={orgId} promotionId={pid}
                                                onSave={onSave} onReload={onReload} />}
                    </div>
                );
            })}

            <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-3 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block">
                        <span className="block text-xs font-medium text-slate-500 mb-1">New promotion name</span>
                        <input value={name} onChange={(e) => setName(e.target.value)}
                               placeholder="Sunday football"
                               data-testid="gg-promo-new-name" className={field} />
                    </label>
                    <label className="block">
                        <span className="block text-xs font-medium text-slate-500 mb-1">League</span>
                        <select value={league} onChange={(e) => setLeague(e.target.value)}
                                data-testid="gg-promo-new-league" className={field}>
                            {leagues.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                        </select>
                    </label>
                </div>
                <button type="button" onClick={create} disabled={busy === 'create'}
                        data-testid="gg-promo-add" className={btn.primary}>
                    {busy === 'create'
                        ? <Loader2 className="animate-spin" size={14} />
                        : <span className="inline-flex items-center gap-1.5"><Plus size={14} /> Add promotion</span>}
                </button>
                <p className="text-[11px] text-slate-500">
                    A new promotion starts with its own four starter prizes, this venue&apos;s
                    window and odds, and nothing else — set it up however you like.
                </p>
            </div>
        </div>
    );
};

export default GoalPromotionBlock;
